import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Tree,
    Table,
    Typography,
    Tag,
    Input,
    Select,
    DatePicker,
    Descriptions,
    Spin,
    Empty,
    Space,
    Button,
    Card,
    Tooltip,
    Banner,
    Row,
    Col,
} from "@douyinfe/semi-ui";
import { IconSearch, IconUser, IconDownload, IconRefresh, IconUserCircle } from "@douyinfe/semi-icons";
import { ApiClient } from "../../api/ApiClient";
import { formatAmountZhCn, formatDate } from "../../utils";

const { Text, Title } = Typography;

let apiClient = ApiClient.getDefault();

interface TreeNodeData {
    key: string;
    label: string;
    outMoneyUserId?: number;
    leadId?: number;
    icon?:any,
    isLeaf?: boolean;
    children?: TreeNodeData[];
}

export default function Reconciliation() {
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [treeData, setTreeData] = useState<TreeNodeData[]>([]);
    const [selectedNode, setSelectedNode] = useState<TreeNodeData | null>(null);
    const [selectedKey, setSelectedKey] = useState<string>("");
    const [statRecord, setStatRecord] = useState<any>(null);
    const [interestDetails, setInterestDetails] = useState<any[]>([]);

    // Filter states
    const [typeFilter, setTypeFilter] = useState<string>("");
    const [interestDateRange, setInterestDateRange] = useState<any>(null);
    const [searchText, setSearchText] = useState("");

    // Accept outMoneyUserId from URL params
    const [initOutMoneyUserId] = useState<number | undefined>(() => {
        const hash = window.location.hash;
        const match = hash.match(/outMoneyUserId=(\d+)/);
        return match ? parseInt(match[1]) : undefined;
    });

    // Full data from API
    const [rawRecords, setRawRecords] = useState<any[]>([]);

    // Tree search
    const [treeSearchText, setTreeSearchText] = useState("");

    // Filtered tree data based on search
    const filteredTreeData = useMemo(() => {
        if (!treeSearchText) return treeData;
        const searchLower = treeSearchText.toLowerCase();
        return treeData
            .map(node => {
                const labelMatch = node.label.toLowerCase().includes(searchLower);
                const matchingChildren = (node.children || []).filter(child =>
                    child.label.toLowerCase().includes(searchLower)
                );
                if (labelMatch || matchingChildren.length > 0) {
                    return {
                        ...node,
                        children: labelMatch ? node.children : matchingChildren,
                    };
                }
                return null;
            })
            .filter(Boolean) as TreeNodeData[];
    }, [treeData, treeSearchText]);

    // Load data from API
    const loadData = useCallback(() => {
        setLoading(true);
        const params: any = { page: 1, size: 500 };
        if (initOutMoneyUserId) {
            params.outMoneyUserId = initOutMoneyUserId;
        }
        apiClient.request("/repaymentPlan/statPaidPlanByOutMoneyUserId", "GET", params)
            .then((resp: any) => {
                const records = resp.data?.records || [];
                setRawRecords(records);

                // Build tree data
                const tree: TreeNodeData[] = records.map((record: any) => {
                    const node: TreeNodeData = {
                        key: `user_${record.outMoneyUserId}`,
                        label: record.outMoneyUserName || `出款人${record.outMoneyUserId}`,
                        outMoneyUserId: record.outMoneyUserId,
                        icon: (<IconUserCircle style={{ color: 'violet' }} />),
                        children: (record.children || []).map((child: any) => {
                            const statusText = child.loanStatus === 'LOANING' ? ' [贷款中]' :
                                child.loanStatus === 'SETTLED' ? ' [已释放]' :
                                child.loanStatus === 'BAD_DEPT' ? ' [已坏账]' : '';
                            return {
                                key: `user_${record.outMoneyUserId}_lead_${child.leadId}`,
                                label: `${child.leadName || "未知客户"}${statusText}`,
                                outMoneyUserId: record.outMoneyUserId,
                                leadId: child.leadId,
                                icon: (<IconUser style={{ color: 'purple' }} />),
                                isLeaf: true,
                            };
                        }),
                    };
                    return node;
                });
                setTreeData(tree);

                // Auto-select first node or the one matching initOutMoneyUserId
                if (tree.length > 0) {
                    const targetNode = initOutMoneyUserId
                        ? tree.find(n => n.outMoneyUserId === initOutMoneyUserId)
                        : tree[0];
                    if (targetNode) {
                        handleNodeSelect(targetNode.key, records);
                    }
                }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [initOutMoneyUserId]);

    // useEffect(() => {
    //     loadData();
    // }, [loadData]);

    // Handle tree node selection
    const handleNodeSelect = (key: string, records?: any[]) => {
        const data = records || rawRecords;
        const isUserNode = key.startsWith("user_") && !key.includes("_lead_");

        setSelectedKey(key);

        if (isUserNode) {
            const userId = parseInt(key.replace("user_", ""));
            const userRecord = data.find((r: any) => r.outMoneyUserId === userId);
            if (userRecord) {
                setSelectedNode({
                    key: key,
                    label: userRecord.outMoneyUserName,
                    outMoneyUserId: userId,
                });
                setStatRecord(userRecord);

                // Merge all children's interest details
                const allDetails: any[] = [];
                (userRecord.children || []).forEach((child: any) => {
                    if (child.mortgageInterestDetails) {
                        child.mortgageInterestDetails.forEach((d: any) => {
                            allDetails.push({ ...d, loanType: d.loanType || "MORTGAGE", leadId: child.leadId, leadName: child.leadName });
                        });
                    }
                    if (child.cardInterestDetails) {
                        child.cardInterestDetails.forEach((d: any) => {
                            allDetails.push({ ...d, loanType: d.loanType || "CARD", leadId: child.leadId, leadName: child.leadName });
                        });
                    }
                    if (child.borrowInterestDetails) {
                        child.borrowInterestDetails.forEach((d: any) => {
                            allDetails.push({ ...d, loanType: d.loanType || "BORROW", leadId: child.leadId, leadName: child.leadName });
                        });
                    }
                });
                allDetails.sort((a, b) => {
                    const dateA = a.interestStartDate ? new Date(a.interestStartDate).getTime() : 0;
                    const dateB = b.interestStartDate ? new Date(b.interestStartDate).getTime() : 0;
                    return dateB - dateA;
                });
                setInterestDetails(allDetails);
            }
        } else {
            // Lead node
            const match = key.match(/user_(\d+)_lead_(\d+)/);
            if (match) {
                const userId = parseInt(match[1]);
                const leadId = parseInt(match[2]);
                const userRecord = data.find((r: any) => r.outMoneyUserId === userId);
                const childRecord = (userRecord?.children || []).find((c: any) => c.leadId === leadId);

                if (childRecord) {
                    setSelectedNode({
                        key: key,
                        label: `${userRecord?.outMoneyUserName} - ${childRecord.leadName}`,
                        outMoneyUserId: userId,
                        leadId,
                    });
                    setStatRecord(childRecord);

                    const details: any[] = [];
                    if (childRecord.mortgageInterestDetails) {
                        childRecord.mortgageInterestDetails.forEach((d: any) => {
                            details.push({ ...d, loanType: d.loanType || "MORTGAGE", leadId: childRecord.leadId, leadName: childRecord.leadName });
                        });
                    }
                    if (childRecord.cardInterestDetails) {
                        childRecord.cardInterestDetails.forEach((d: any) => {
                            details.push({ ...d, loanType: d.loanType || "CARD", leadId: childRecord.leadId, leadName: childRecord.leadName });
                        });
                    }
                    if (childRecord.borrowInterestDetails) {
                        childRecord.borrowInterestDetails.forEach((d: any) => {
                            details.push({ ...d, loanType: d.loanType || "BORROW", leadId: childRecord.leadId, leadName: childRecord.leadName });
                        });
                    }
                    details.sort((a, b) => {
                        const dateA = a.interestStartDate ? new Date(a.interestStartDate).getTime() : 0;
                        const dateB = b.interestStartDate ? new Date(b.interestStartDate).getTime() : 0;
                        return dateB - dateA;
                    });
                    setInterestDetails(details);
                }
            }
        }
    };

    // Export handler
    const handleExport = async () => {
        if (!selectedNode?.outMoneyUserId) return;
        setExporting(true);
        try {
            const params: any = { outMoneyUserId: selectedNode.outMoneyUserId };
            const resp:any = await apiClient.request(
                "/repaymentPlan/statPaidPlanByOutMoneyUserId/export",
                "GET",
                params
            );
            if (resp.data) {
                window.open(resp.data, "_blank");
            }
        } catch (e) {
            console.error("导出失败", e);
        }
        setExporting(false);
    };

    // Render loan type tag
    const renderLoanTypeTag = (type: string) => {
        const typeConfig: Record<string, { color: any; text: string }> = {
            "MORTGAGE": { color: "blue", text: "信贷" },
            "CARD": { color: "green", text: "信用卡" },
            "BORROW": { color: "orange", text: "借款" },
        };
        const config = typeConfig[type] || { color: "default", text: type };
        return <Tag color={config.color} size="small">{config.text}</Tag>;
    };

    // Filter interest details
    const filteredDetails = useMemo(() => {
        return interestDetails.filter((item) => {
            if (typeFilter && item.loanType !== typeFilter) {
                return false;
            }
            if (interestDateRange && interestDateRange.length === 2) {
                const itemDate = item.interestStartDate ? new Date(item.interestStartDate).getTime() : 0;
                const start = new Date(interestDateRange[0]).getTime();
                const end = new Date(interestDateRange[1]).getTime();
                if (itemDate < start || itemDate > end) {
                    return false;
                }
            }
            if (searchText) {
                const searchLower = searchText.toLowerCase();
                const platform = (item.platform || "").toLowerCase();
                const loanNo = (item.loanNo || "").toLowerCase();
                if (!platform.includes(searchLower) && !loanNo.includes(searchLower)) {
                    return false;
                }
            }
            return true;
        });
    }, [interestDetails, typeFilter, interestDateRange, searchText]);

    // Computed: total interest and commission from filtered details
    const filteredSummary = useMemo(() => {
        const totalInterest = filteredDetails.reduce((sum, item) => sum + Number(item.interest || 0), 0);
        const totalCommission = filteredDetails.reduce((sum, item) => sum + Number(item.commission || 0), 0);
        return { totalInterest, totalCommission };
    }, [filteredDetails]);

    // Statistics summary for the selected node
    const statSummary = useMemo(() => {
        if (!statRecord) return [];
        const r = statRecord;
        return [
            { key: "出款金额", value: formatAmountZhCn(r.outMoneyAmount) },
            { key: "贷款代还金额", value: formatAmountZhCn(r.loanHelpRepaymentPlanAmount) },
            { key: "信用卡代还金额", value: formatAmountZhCn(r.cardHelpRepaymentPlanAmount) },
            { key: "过账代还金额", value: formatAmountZhCn(r.postingHelpAmount) },
            { key: "借出金额", value: formatAmountZhCn(r.borrowAmount) },
            { key: "借出归还金额", value: formatAmountZhCn(r.borrowReturnAmount) },
            { key: "利息", value: <Text type="warning" strong style={{fontSize:"20px"}}>{formatAmountZhCn(r.interestAmount)}</Text> },
            { key: "佣金", value: formatAmountZhCn(r.commissionAmount) },
        ];
    }, [statRecord]);

    // Interest detail table columns
    const interestColumns = [
        {
            title: "类型",
            dataIndex: "loanType",
            key: "loanType",
            width: 80,
            fixed: "left" as const,
            render: (value: string) => renderLoanTypeTag(value),
        },
        {
            title: "客户",
            dataIndex: "leadName",
            key: "leadName",
            width: 80,
        },
        {
            title: "平台",
            dataIndex: "platform",
            key: "platform",
            width: 100,
            render: (value: string) => value || <Text type="tertiary">-</Text>,
        },
        {
            title: "贷款编号",
            dataIndex: "loanNo",
            key: "loanNo",
            width: 110,
            render: (value: string) => value || <Text type="tertiary">-</Text>,
        },
        {
            title: "计息日期",
            dataIndex: "interestStartDate",
            key: "interestStartDate",
            width: 110,
            sorter: (a: any, b: any) => (a.interestStartDate > b.interestStartDate ? 1 : -1),
            render: (value: string) => value ? <Text strong>{formatDate(value)}</Text> : "-",
        },
        {
            title: "利息天数",
            dataIndex: "interestDays",
            key: "interestDays",
            width: 180,
            render: (value: number, row: any) => {
                if (row.paidMethod === "C_GZ_DH") {
                    return <Tag color="purple" size="small">过账代还</Tag>;
                } else {
                    return value
                        ? <span>至{formatDate(row.interestEndDate)}({row.interestEndDateDesc}) <Text strong>{value}天</Text></span>
                        : "-";
                }
            },
        },
        {
            title: "计息金额",
            dataIndex: "interestAmount",
            key: "interestAmount",
            width: 110,
            align: "right" as const,
            render: (value: number) => formatAmountZhCn(value),
        },
        {
            title: "利率",
            dataIndex: "interestRate",
            key: "interestRate",
            width: 80,
            align: "right" as const,
        },
        {
            title: "利息",
            dataIndex: "interest",
            key: "interest",
            width: 110,
            align: "right" as const,
            render: (value: number) => (
                <Text type="warning" strong>{formatAmountZhCn(value || 0)}</Text>
            ),
        },
        {
            title: "还款方式",
            dataIndex: "paidMethodName",
            key: "paidMethodName",
            width: 100,
            render: (value: string) => value || <Text type="tertiary">-</Text>,
        },
    ];

    return (
        <>
        <Row gutter={8}>
            <Col span={18}>
            <Tooltip content="使用前请点击加载数据">
               <Button
                                    icon={<IconRefresh />}
                                    theme="outline"
                                    type="warning"
                                    onClick={loadData}
                                    loading={loading}
                                >加载所有数据
                                    </Button>
            </Tooltip>
            <Tag style={{ marginBottom: "5px", marginLeft: "5px" }} color="light-blue">使用前请点击加载数据</Tag>
            </Col>
        </Row>
        <div style={{ display: "flex", height: "calc(100vh - 20px)", gap: 12, padding: 0 }}>
            {/* Left panel - Tree */}
            <Card
                title={
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                       <Input
                    prefix={<IconSearch />}
                    placeholder="搜索出款人/客户..."
                    value={treeSearchText}
                    onChange={setTreeSearchText}
                    showClear
       
                />
                        <Tag style={{ marginLeft: "auto" }}>{treeData.length} 人</Tag>
                    </div>
                }
                style={{ width: 260, minWidth: 220, flexShrink: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}
                bodyStyle={{ padding: "8px 12px", overflow: "auto", flex: 1 }}
            >
                
                <Spin spinning={loading}>
                    {filteredTreeData.length > 0 ? (
                        <Tree
                            treeData={filteredTreeData}
                            value={selectedKey}
                            onChange={(key: string) => {
                                if (key) {
                                    handleNodeSelect(key as string);
                                }
                            }}
                            
                            showLine
                            style={{ fontSize: 13 }}
                        />
                    ) : (
                        <Empty description="暂无数据" style={{ marginTop: 40 }} />
                    )}
                </Spin>
            </Card>

            {/* Right panel */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12, overflow: "hidden" }}>
                {/* Top - Statistics summary */}
                <Card
                    title={
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 14, fontWeight: 600 }}>
                                {selectedNode ? `统计 - ${selectedNode.label}` : "统计"}
                            </span>
                            {selectedNode?.outMoneyUserId && (
                                <>
                                 <Tag style={{ marginLeft: "auto" }} color="light-blue">导出选择的出款人的所有出款明细，用于对账</Tag>
                                <Tooltip content="导出该出款人的对账明细">
                                    <Button
                                        icon={<IconDownload />}
                                        size="small"
                                        theme="outline"
                                        type="warning"
                                        loading={exporting}
                                        onClick={handleExport}
                                        
                                    >
                                        导出明细
                                    </Button>
                                </Tooltip>
                               
                                </>
                            )}
                           
                        </div>
                    }
                    style={{ flexShrink: 0 }}
                    bodyStyle={{ padding: "12px 16px" }}
                >
                    {statRecord ? (
                        <Descriptions
                            data={statSummary}
                            row
                            size="medium"
                            style={{
                                backgroundColor: "var(--semi-color-bg-2)",
                                borderRadius: "8px",
                                padding: "12px 16px",
                            }}
                        />
                    ) : (
                        <Banner
                            type="info"
                            description="请选择左侧出款人或客户节点查看统计数据"
                            fullMode={false}
                        />
                    )}
                </Card>

                {/* Bottom - Interest details */}
                <Card
                    title={
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 14, fontWeight: 600 }}>利息计算明细</span>
                            <Tag size="small" color="grey">共 {filteredDetails.length} 笔</Tag>
                            {typeFilter && (
                                <Tag size="small" color="blue">
                                    {typeFilter === "MORTGAGE" ? "信贷" : typeFilter === "CARD" ? "信用卡" : "借款"}
                                </Tag>
                            )}
                            {filteredDetails.length > 0 && (
                                <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--semi-color-text-2)" }}>
                                    筛选利息合计: <Text type="warning" strong>{formatAmountZhCn(filteredSummary.totalInterest)}</Text>
                                </span>
                            )}
                        </div>
                    }
                    style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}
                    bodyStyle={{ padding: "8px 12px", flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}
                >
                    {/* Filter bar */}
                    <div style={{
                        display: "flex",
                        gap: 8,
                        marginBottom: 8,
                        flexWrap: "wrap",
                        alignItems: "center",
                        padding: "8px 12px",
                        backgroundColor: "var(--semi-color-bg-2)",
                        borderRadius: "8px",
                    }}>
                        {/* Quick type filter buttons */}
                        <span style={{ color: "var(--semi-color-text-2)", fontSize: 12, fontWeight: 500 }}>类型:</span>
                        <ButtonGroup selected={typeFilter} onChange={setTypeFilter} />
                        <div style={{ width: 1, height: 20, backgroundColor: "var(--semi-color-border)", margin: "0 4px" }} />
                        <DatePicker
                            type="dateRange"
                            placeholder={["计息开始日期", "结束日期"]}
                            value={interestDateRange}
                            onChange={setInterestDateRange}
                            style={{ width: 240 }}
                            size="small"
                        />
                        <Input
                            prefix={<IconSearch />}
                            placeholder="搜索平台或贷款编号..."
                            value={searchText}
                            onChange={setSearchText}
                            showClear
                            style={{ width: 200 }}
                            size="small"
                        />
                        {(typeFilter || searchText || interestDateRange) && (
                            <Button
                                size="small"
                                theme="borderless"
                                onClick={() => { setTypeFilter(""); setSearchText(""); setInterestDateRange(null); }}
                            >
                                清除筛选
                            </Button>
                        )}
                    </div>

                    {/* Table */}
                    <div style={{ flex: 1, overflow: "auto" }}>
                        <Table
                            bordered
                            columns={interestColumns}
                            dataSource={filteredDetails}
                            rowKey="planId"
                            pagination={false}
                            scroll={{ y: "calc(100vh - 440px)", x: 1050 }}
                            size="small"
                            empty={
                                <Empty description="暂无利息明细数据" style={{ padding: 40 }} />
                            }
                        />
                    </div>
                </Card>
            </div>
        </div>
    </>
    );
}

// Quick type filter button group
function ButtonGroup({ selected, onChange }: { selected: string; onChange: (v: string) => void }) {
    const items = [
        { value: "", label: "全部" },
        { value: "MORTGAGE", label: "信贷", color: "blue" },
        { value: "CARD", label: "信用卡", color: "green" },
        { value: "BORROW", label: "借款", color: "orange" },
    ];
    return (
        <Space >
            {items.map(item => (
                <Button
                    key={item.value}
                    size="small"
                    theme={selected === item.value ? "solid" : "light"}
                    onClick={() => onChange(item.value)}
                    style={selected === item.value && item.color ? {
                        backgroundColor: `var(--semi-color-${item.color})`,
                        borderColor: `var(--semi-color-${item.color})`,
                        color: "#fff",
                    } : undefined}
                >
                    {item.label}
                </Button>
            ))}
        </Space>
    );
}
